import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Fieldset,
  Details,
  Radios,
  Button,
  ErrorSummary,
} from 'govuk-react-jsx';
import {
  setIsLinkedEnterprise,
  getIsLinkedEnterprise,
} from '../../features/registration/registrationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { updateAccount } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const LinkedEnterprise = () => {
  const isLinkedEnterprise = useSelector(getIsLinkedEnterprise);
  const dispatch = useDispatch();
  const [hasError, setHasError] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const history = useHistory();
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  useTitle(
    'landlordReg.LinkedEnterprise.s29',
    'Is your company part of a linked enterprise? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const errorMsg = copyProvider.getCopy(
    'landlordReg.LinkedEnterprise.s1',
    'Is your company part of a linked enterprise?'
  );

  const handleBtnClick = () => {
    if (!isLinkedEnterprise) {
      setHasError(true);
      setErrorList([{ children: errorMsg, href: '#yes' }]);
    } else {
      updateAccount({
        isLinkedEnterprise: isLinkedEnterprise === 'yes',
      })
        .then(() => {
          setHasError(false);

          if (isLinkedEnterprise === 'yes') {
            // page to push to is TBD
            history.push('/landlordReg/linked-enterprise-number');
          } else {
            history.push('/landlordReg/company-registration-number');
          }
        })
        .catch(() => {
          setHasError(true);
          setErrorList([{ children: errorMsg, href: '#yes' }]);
        });
    }
  };

  const handleSelection = (event) => {
    dispatch(setIsLinkedEnterprise(event.target.value));
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <Fieldset
        legend={{
          children: (
            <h1 className="govuk-fieldset__heading">
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s1',
                'Is your company part if a linked enterprise?'
              )}
            </h1>
          ),
          className: 'govuk-fieldset__legend govuk-fieldset__legend--xl',
        }}
      >
        <div className="govuk-body govuk-!-font-size-19 govuk-!-margin-bottom-5">
          <section>
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s9',
              'A linked enterprise is directly or indirectly owned or controlled by another enterprise.'
            )}
          </section>
        </div>
        <Radios
          value={isLinkedEnterprise}
          onClick={(event) => handleSelection(event)}
          errorMessage={
            hasError && {
              children: errorMsg,
            }
          }
          idPrefix="example"
          items={[
            {
              children: copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s4',
                'Yes'
              ),
              value: 'yes',
              id: 'yes',
            },
            {
              children: copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s5',
                'No'
              ),
              value: 'No',
              id: 'no',
            },
          ]}
        />
        <Details
          className="govuk-details"
          summaryChildren={copyProvider.getCopy(
            'landlordReg.LinkedEnterprise.s7',
            'What is a linked enterprise'
          )}
        >
          <p className="govuk-body">
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s2',
              'Linked enterprises are enterprises which have any of the following relationships with each other:'
            )}
          </p>
          <ul className="govuk-list govuk-list--bullet">
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s10',
                'questions, queries or feedback you leave'
              )}
            </li>
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s11',
                'if your company is independent and it does not have any other company participating in it, then it is not a linked enterprise'
              )}
            </li>
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s12',
                'if another company or enterprise has a large stake in your company, it is likely that your company is a linked enterprise'
              )}
            </li>
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s13',
                'if another company or enterprise controls your company it is likely that your company is a linked enterprise'
              )}
            </li>
          </ul>
          <h3 className="govuk-heading-s">
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s14',
              'Example of a linked enterprise:'
            )}
          </h3>
          <p className="govuk-body">
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s15',
              'Volkswagen Group UK is a company that works like a linked enterprise (or controlling enterprise). Some of its brands include:'
            )}
          </p>
          <ul className="govuk-list govuk-list--bullet">
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s16',
                'Volkswagen'
              )}
            </li>
            <li>
              {copyProvider.getCopy('landlordReg.LinkedEnterprise.s17', 'Audi')}
            </li>
            <li>
              {copyProvider.getCopy('landlordReg.LinkedEnterprise.s18', 'SEAT')}
            </li>
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s19',
                'ŠKODA'
              )}
            </li>
            <li>
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s20',
                'Volkswagen Commercial Vehicles'
              )}
            </li>
          </ul>
          <p className="govuk-body govuk-!-font-size-19 govuk-!-margin-top-5">
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s21',
              "Take Audi as an example. If Audi want to apply for a chargepoint grant they need to use the Volkswagen Group UK number. That is their controlling company's number, not their Audi Companies House number."
            )}
          </p>
          <p className="govuk-body govuk-!-font-size-19 govuk-!-margin-top-5">
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s22',
              'If you are still unsure about being a linked enterprise there is more information in the '
            )}
            <a
              href="https://www.gov.uk/hmrc-internal-manuals/international-manual/intm412100"
              target="_blank"
              className="govuk-link"
              rel="noreferrer"
            >
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s23',
                'HMRC internal manual'
              )}
            </a>
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s24',
              ' and the '
            )}
            <a
              href="https://ec.europa.eu/growth/smes/sme-definition_en"
              target="_blank"
              className="govuk-link"
              rel="noreferrer"
            >
              {copyProvider.getCopy(
                'landlordReg.LinkedEnterprise.s25',
                'European Commission User guide'
              )}
            </a>
            {copyProvider.getCopy(
              'landlordReg.LinkedEnterprise.s26',
              ' to the SME Definition.'
            )}
          </p>
        </Details>
      </Fieldset>

      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy('landlordReg.LinkedEnterprise.s6', 'Continue')}
      </Button>
    </>
  );

  return pageModel;
};

export default LinkedEnterprise;
